import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "sonner";
import { At, Trash, ChatCircleDots } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

function timeAgo(iso) {
  const t = (Date.now() - new Date(iso).getTime()) / 1000;
  if (t < 60) return "just now";
  if (t < 3600) return `${Math.floor(t / 60)}m ago`;
  if (t < 86400) return `${Math.floor(t / 3600)}h ago`;
  return new Date(iso).toLocaleDateString();
}

export default function CommentsDrawer({ open, onOpenChange, proposalId, proposalOwnerId }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [draft, setDraft] = useState("");
  const [mentions, setMentions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const textRef = useRef(null);

  const load = () => {
    api.get(`/proposals/${proposalId}/comments`).then((r) => setItems(r.data)).catch(() => {});
    api.get(`/proposals/${proposalId}/collaborators`).then((r) => setCollaborators(r.data)).catch(() => setCollaborators([]));
  };

  useEffect(() => {
    if (open && proposalId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, proposalId]);

  const toggleMention = (m) => {
    setMentions((prev) =>
      prev.find((x) => x.id === m.id)
        ? prev.filter((x) => x.id !== m.id)
        : [...prev, m]
    );
  };

  const submit = async () => {
    if (!draft.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/proposals/${proposalId}/comments`, {
        content: draft,
        mentioned_user_ids: mentions.map((m) => m.id),
      });
      setItems((prev) => [...prev, data]);
      setDraft("");
      setMentions([]);
      if (mentions.length) {
        toast.success(`Comment posted — ${mentions.length} ${mentions.length === 1 ? "person" : "people"} notified`);
      } else {
        toast.success("Comment posted");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not post");
    } finally {
      setBusy(false);
    }
  };

  const del = async (c) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/proposals/${proposalId}/comments/${c.id}`);
      setItems((prev) => prev.filter((x) => x.id !== c.id));
      toast.success("Deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const canDelete = (c) => {
    if (!user) return false;
    return c.author_id === user.id
      || proposalOwnerId === user.id
      || (user.company_role === "manager");
  };

  const collaboratorMap = collaborators.reduce((acc, c) => {
    acc[c.id] = c;
    return acc;
  }, {});

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="gf-sharp w-full sm:max-w-lg flex flex-col p-0 border-l border-zinc-200"
        data-testid="comments-drawer"
      >
        <SheetHeader className="px-6 py-4 border-b border-zinc-200">
          <SheetTitle className="font-display font-bold tracking-tight flex items-center gap-2">
            <ChatCircleDots size={20} /> Review thread
          </SheetTitle>
          <SheetDescription>
            Comment + @mention company members. They'll get an email.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4" data-testid="comments-list">
          {items.length === 0 ? (
            <div className="text-center py-12">
              <ChatCircleDots size={28} className="mx-auto text-zinc-400" />
              <div className="gf-overline mt-3">No comments yet</div>
              <p className="text-zinc-500 mt-2 text-sm">Start the conversation below.</p>
            </div>
          ) : (
            items.map((c) => (
              <div key={c.id} className="border border-zinc-200 bg-white p-4" data-testid={`comment-${c.id}`}>
                <div className="flex items-baseline justify-between mb-2 gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm truncate">{c.author_name || c.author_email}</div>
                    <div className="text-[11px] font-mono text-zinc-500">{timeAgo(c.created_at)}</div>
                  </div>
                  {canDelete(c) && (
                    <button
                      onClick={() => del(c)}
                      data-testid={`comment-delete-${c.id}`}
                      title="Delete comment"
                      className="text-red-700 hover:text-red-900 shrink-0"
                    >
                      <Trash size={14} />
                    </button>
                  )}
                </div>
                <div className="text-sm whitespace-pre-wrap text-zinc-800">{c.content}</div>
                {c.mentioned_user_ids?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {c.mentioned_user_ids.map((uid) => {
                      const m = collaboratorMap[uid];
                      const label = m ? (m.name || m.email) : "member";
                      return (
                        <span
                          key={uid}
                          className="text-[10px] uppercase tracking-wider bg-blue-50 text-blue-700 border border-blue-200 px-1.5 py-0.5 font-mono"
                        >
                          @{label}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        <div className="border-t border-zinc-200 bg-white p-4 space-y-3">
          {mentions.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="active-mentions">
              {mentions.map((m) => (
                <button
                  key={m.id}
                  onClick={() => toggleMention(m)}
                  className="text-[10px] uppercase tracking-wider bg-blue-700 text-white px-2 py-0.5 font-mono hover:bg-blue-800"
                  data-testid={`active-mention-${m.id}`}
                >
                  @{m.name || m.email} ×
                </button>
              ))}
            </div>
          )}
          <Textarea
            ref={textRef}
            data-testid="comment-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Leave a review note…"
            rows={3}
            className="gf-sharp border-zinc-300 bg-white text-sm"
          />
          <div className="flex items-center justify-between">
            <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  data-testid="mention-picker-btn"
                  disabled={collaborators.length === 0}
                  className="gf-sharp border-zinc-300 uppercase tracking-wide text-xs font-bold"
                >
                  <At size={14} className="mr-1.5" />
                  {collaborators.length === 0 ? "No teammates" : "Mention"}
                </Button>
              </PopoverTrigger>
              <PopoverContent
                side="top"
                align="start"
                className="gf-sharp w-64 p-0 border-zinc-200"
                data-testid="mention-picker"
              >
                <div className="max-h-64 overflow-y-auto">
                  {collaborators.map((m) => {
                    const active = !!mentions.find((x) => x.id === m.id);
                    return (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => toggleMention(m)}
                        data-testid={`mention-option-${m.id}`}
                        className={`w-full text-left px-3 py-2 hover:bg-zinc-50 border-b border-zinc-100 ${active ? "bg-blue-50" : ""}`}
                      >
                        <div className="font-semibold text-sm">{m.name}</div>
                        <div className="text-[11px] font-mono text-zinc-500">{m.email}</div>
                      </button>
                    );
                  })}
                </div>
              </PopoverContent>
            </Popover>
            <Button
              onClick={submit}
              disabled={busy || !draft.trim()}
              data-testid="comment-submit"
              className="gf-sharp bg-blue-700 hover:bg-blue-800 text-white font-bold uppercase tracking-wide"
            >
              {busy ? "Posting…" : "Post"}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
